import React, { useState, useRef } from 'react';
import { Link } from 'react-router-dom';
import {
  Container,
  Typography,
  Box,
  Card,
  CardContent,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Button,
  Collapse,
  Slider,
} from '@mui/material';
import FlightIcon from '@mui/icons-material/Flight';
import CardGiftcardIcon from '@mui/icons-material/EventAvailable';
import RadioButtonUncheckedIcon from '@mui/icons-material/RadioButtonUnchecked';
import RadioButtonCheckedIcon from '@mui/icons-material/RadioButtonChecked';
import ToggleCard from '../components/ToggleCard';

const Travel: React.FC = () => {
  type ImpactOption = 'mangroves' | 'education' | 'plastic';
  const [bookingConfirmed, setBookingConfirmed] = useState(false);
  const confirmRef = useRef<HTMLDivElement>(null);
  const [impactOption, setImpactOption] = useState<ImpactOption>('mangroves');
  const [contribution, setContribution] = useState(5);

  // Default flight fields
  const fromCity = 'Singapore';
  const toCity = 'Paris';
  const todayDate = new Date();
  const departureDate = todayDate.toISOString().slice(0, 10);
  const returnDateObj = new Date(todayDate);
  returnDateObj.setDate(todayDate.getDate() + 14);
  const returnDate = returnDateObj.toISOString().slice(0, 10);

  const handleBooking = () => setBookingConfirmed(true);

  const impactData: Record<ImpactOption, { emoji: string; label: string; description: string }> = {
    mangroves: { emoji: '🌱', label: 'Restore Mangroves in Indonesia', description: 'Restores coastal ecosystems' },
    education: { emoji: '📚', label: "Fund a Child's Education in Cambodia", description: 'Supports sustainable community development' },
    plastic: { emoji: '🌊', label: 'Clean up ocean plastic', description: 'Removes plastics from the oceans' },
  };

  // Preview mapping based on selected project
  const previewData: Record<ImpactOption, { value: string; unit: string, emoji: string }> = {
    mangroves: { value: `${contribution}`, unit: 'mangroves restored', emoji: '🌱' },
    education: { value: `${contribution}`, unit: 'days of education', emoji: '📚' },
    plastic: { value: `${contribution * 2}`, unit: 'kg plastic removed', emoji: '🌊' },
  };

  return (
    <Container maxWidth="lg">
      <Typography variant="h3" sx={{ color: 'rgb(79,111,82)', mb: 2 }}>Flight Checkout</Typography>
      <Typography variant="subtitle1" color="text.primary" sx={{ mb: 4 }}>
        Book your flights while supporting regenerative projects worldwide with our API
      </Typography>

      <Box display="flex" flexDirection={{ xs: 'column', sm: 'row' }} gap={4}>
        {/* Booking Form */}
        <Box flex={1}>
          <Card>
            <CardContent sx={{ p: 4 }}>
              <Typography variant="h5" sx={{ mb: 3, fontWeight: 700, display: 'flex', alignItems: 'center' }}>
                <FlightIcon sx={{ mr: 1, color: 'rgb(101,183,65)' }} /> Book Your Flight
              </Typography>

              <Box component="form" sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                <TextField fullWidth disabled label="From" value={fromCity} />
                <TextField fullWidth disabled label="To" value={toCity} />
                <Box sx={{ display: 'flex', gap: 2, flexDirection: { xs: 'column', sm: 'row' } }}>
                  <TextField
                    fullWidth
                    disabled
                    label="Departure Date"
                    type="date"
                    InputLabelProps={{ shrink: true }}
                    value={departureDate}
                  />
                  <TextField
                    fullWidth
                    disabled
                    label="Return Date"
                    type="date"
                    InputLabelProps={{ shrink: true }}
                    value={returnDate}
                  />
                </Box>
                <FormControl fullWidth disabled>
                  <InputLabel>Passengers</InputLabel>
                  <Select label="Passengers" defaultValue={1}>
                    {[1, 2, 3, 4].map((n) => (
                      <MenuItem key={n} value={n}>{n} passenger{n > 1 ? 's' : ''}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
                <FormControl fullWidth disabled>
                  <InputLabel>Class</InputLabel>
                  <Select label="Class" defaultValue="Economy">
                    {['Economy', 'Business', 'First'].map((c) => (
                      <MenuItem key={c} value={c}>{c}</MenuItem>
                    ))}
                  </Select>
                </FormControl>

                {/* Regenerative Impact Section */}
                <ToggleCard title="Regenerative Impact" code={{ impact: impactData }}>
                  {(['mangroves', 'education', 'plastic'] as ImpactOption[]).map(key => (
                    <Box
                      key={key}
                      onClick={() => !bookingConfirmed && setImpactOption(key)}
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        cursor: bookingConfirmed ? 'default' : 'pointer',
                        pointerEvents: bookingConfirmed ? 'none' : 'auto',
                        borderRadius: 1,
                        p: 1,
                        mb: 0.5,
                        backgroundColor: impactOption === key ? 'rgba(101,183,65,0.1)' : '#fff',
                      }}>


                      {impactOption === key ? (
                        <RadioButtonCheckedIcon sx={{
                          color: bookingConfirmed ? 'rgba(0,0,0,0.26)' : 'rgb(101,183,65)',
                          mr: 1
                        }} />
                      ) : (
                        <RadioButtonUncheckedIcon
                          sx={{
                            color: bookingConfirmed ? 'rgba(0,0,0,0.26)' : 'rgb(101,183,65)',
                            mr: 1
                          }}
                        />
                      )}
                      <Box display="flex" alignItems="center">
                        <Typography sx={{ fontSize: 20, mr: 1 }}>{impactData[key].emoji}</Typography>
                        <Box>
                          <Typography sx={{ fontWeight: 600, color: 'rgb(33,33,33)' }}>{impactData[key].label}</Typography>
                          <Typography variant="body2" sx={{ color: 'rgb(117,117,117)' }}>{impactData[key].description}</Typography>
                        </Box>
                      </Box>
                    </Box>
                  ))}
                </ToggleCard>

                {/* Contribution Slider */}
                <Box sx={{ mt: 3 }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                    <Typography>Contribution Amount ($)</Typography>
                    <Typography sx={{ fontWeight: 600 }}>${contribution}</Typography>
                  </Box>
                  <Slider
                    value={contribution}
                    disabled={bookingConfirmed}
                    min={1}
                    max={50}
                    onChange={(e, val) => setContribution(val as number)}
                    valueLabelDisplay="auto"
                    sx={{ color: 'rgb(101,183,65)' }}
                  />
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 0.5 }}>
                    <Typography>$1</Typography>
                    <Typography>$50</Typography>
                  </Box>
                </Box>

                {/* Flight Details Table */}
                <Box sx={{ border: '1px solid rgb(209,230,218)', borderRadius: 2, p: 2, mb: 3 }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                    <Typography variant="body1" sx={{ fontWeight: 600 }}>Flight Cost</Typography>
                    <Typography variant="body1" sx={{ fontWeight: 600 }}>Regenerative Impact</Typography>
                  </Box>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                    <Typography variant="h6" sx={{ fontWeight: 700 }}>$324.00</Typography>
                    <Typography variant="h6" sx={{ fontWeight: 700, color: 'rgb(101,183,65)' }}>+${contribution.toFixed(2)}</Typography>
                  </Box>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', pt: 1, borderTop: '1px solid rgb(209,230,218)' }}>
                    <Typography variant="body1" sx={{ fontWeight: 700 }}>Total:</Typography>
                    <Typography variant="body1" sx={{ fontWeight: 700 }}>${(324 + contribution).toFixed(2)}</Typography>
                  </Box>
                </Box>

                {/* Confirm Button */}
                <Button
                  variant="contained"
                  disabled={bookingConfirmed}
                  color="primary"
                  fullWidth
                  sx={{ py: 1.5, fontWeight: 700, mt: 3 }}
                  onClick={handleBooking}
                >
                  Confirm Booking
                </Button>
              </Box>
            </CardContent>
          </Card>
        </Box>

        {/* Confirmation Section */}
        <Box flex={1} ref={confirmRef} sx={{ scrollMarginTop: '64px' }}>
          <Collapse in={bookingConfirmed} timeout={600} unmountOnExit onEntered={() => confirmRef.current?.scrollIntoView({ behavior: 'smooth' })}>
            <Card sx={{ borderRadius: 2, boxShadow: 1 }}>
              <CardContent sx={{ p: 4 }}>
                <Typography
                  variant="h5"
                  sx={{
                    fontWeight: 700,
                    display: 'flex',
                    alignItems: 'center',
                    mb: 2,
                    color: 'rgb(79,111,82)'
                  }}
                >
                  <CardGiftcardIcon sx={{ mr: 1, color: 'rgb(101,183,65)' }} /> Booking Confirmed
                </Typography>

                <Typography variant="subtitle1" sx={{ mb: 2 }}>
                  Your booking is confirmed! See how your flight is making a difference.
                </Typography>

                <ToggleCard title="&nbsp;" code={{ impact: previewData[impactOption] }} sx={{ backgroundColor: 'rgba(239,253,219,0.6)', borderRadius: 2, p: 3, textAlign: 'center', mb: 3 }}>
                  <Box sx={{ width: 72, height: 72, borderRadius: '50%', backgroundColor: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', mx: 'auto', mb: 2 }}>
                    <Typography sx={{ fontSize: 20, mr: 1 }}>{previewData[impactOption].emoji}</Typography>
                  </Box>

                  <Typography variant="h4" sx={{ color: 'rgb(79,111,82)', fontWeight: 700, mb: 1 }}>
                    {previewData[impactOption].value} {previewData[impactOption].unit}
                  </Typography>
                  <Typography>{impactData[impactOption].description}</Typography>
                </ToggleCard>

                {/* Amplify Your Impact */}
                <Box sx={{ backgroundColor: 'rgba(239,253,219,0.6)', borderRadius: 2, p: 3, mb: 3 }}>
                  <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 1 }}>Amplify Your Impact</Typography>
                  <Typography variant="body2" sx={{ color: 'rgb(33,33,33)', mb: 2 }}>Share your contribution to inspire others</Typography>
                  <Box sx={{ display: 'flex', gap: 2 }}>
                    <Button variant="outlined" fullWidth sx={{ borderColor: 'rgb(101,183,65)', color: 'rgb(101,183,65)', textTransform: 'none' }}>Share</Button>
                  </Box>
                </Box>
                {/* Tracking CTA */}
                <Box
                  component={Link}
                  to="/engagement"
                  sx={{
                    textDecoration: 'none',     // remove underline
                    color: 'inherit',           // inherit text colour
                    backgroundColor: 'rgb(239,253,219)',
                    p: 2,
                    borderRadius: 1,
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    cursor: 'pointer',
                    '&:hover': {
                      backgroundColor: 'rgb(227,244,204)',
                      textDecoration: 'none'    // ensure no underline on hover
                    }
                  }}
                >
                  <Typography fontWeight={700}>Track Your Impact <span style={{ fontWeight: 400 }}>- get exciting updates and stories!</span></Typography>
                  <Typography>{'→'}</Typography>
                </Box>
              </CardContent>
            </Card>
          </Collapse>
        </Box>
      </Box>
    </Container>
  );
};

export default Travel;